import {
  expectedIcDph,
  icDphMatchesDic,
  isSlovakIban,
  isValidDic,
  isValidIban,
  isValidIcDph,
  isValidIco,
  tt,
} from '@agos/shared'

/**
 * The supplier fields that are not free text, however freely legacy treats them.
 *
 * A supplier record holds the **IBAN money is paid to**, the **IČO** a tax filing
 * quotes and the **IČ DPH** a VAT return is reconciled against. Legacy validates none
 * of them: every one is an `<input>` that accepts anything, and a transposed digit in
 * an IBAN is a payment to nowhere that nobody notices until the supplier calls.
 *
 * All three have deterministic check digits, so the frontend can say so before the
 * record is saved. **FB-080.**
 *
 * The check digits themselves live in `@agos/shared/business-identity` — they moved
 * there when `manager-invoicing` needed the same IBAN check, and §5 forbids one module
 * importing another. What stays here is what a *supplier record* makes of them.
 */

// The 24 fields this used to declare are the backend's now — `Supplier` in the generated model,
// including the `| null`s that made a hand-written copy necessary in the first place (BLOCKED #4).
// Re-exported rather than re-imported everywhere, so the twenty call sites in this module stay as
// they are and there is still exactly one definition.
import type { Supplier } from '@agos/api-client/model'

export type { Supplier }

/**
 * A supplier being edited, which is not yet a supplier the API answered.
 *
 * `Supplier` gained a `required` list from its columns on 2026-08-03, so it now means *a row the
 * endpoint returned* — id, name, country, active and both timestamps guaranteed. A blank form has
 * none of them, and the validators below run **while** somebody is typing, on exactly that.
 *
 * Distinguishing the two is the point rather than a workaround: the page held its new-supplier form
 * in a `Supplier` and nothing objected, because before the required marks the type promised nothing.
 */
export type SupplierDraft = Partial<Supplier>

export type IdentityProblem =
  | { kind: 'ico-checksum'; value: string }
  | { kind: 'dic-length'; value: string }
  | { kind: 'ic-dph-shape'; value: string }
  | { kind: 'ic-dph-mismatch'; icDph: string; expected: string }
  | { kind: 'iban-checksum'; value: string }
  | { kind: 'iban-length'; value: string }

/**
 * Everything wrong with a supplier's identifiers.
 *
 * All of them are **warnings**, not refusals. A foreign supplier has no Slovak IČO, a
 * new record legitimately starts empty — the KMV BEV SK row in the data has nothing
 * but a name and a note saying to fill the rest in from the PDF — and a supplier
 * created by the invoice importer is meant to be completed later, not blocked. What
 * must not happen is a wrong IBAN saved in silence.
 */
export function identityProblems(supplier: SupplierDraft): IdentityProblem[] {
  const problems: IdentityProblem[] = []

  const ico = (supplier.ico ?? '').trim()
  if (ico !== '' && !isValidIco(ico)) {
    problems.push({ kind: 'ico-checksum', value: ico })
  }

  const dic = (supplier.dic ?? '').trim()
  if (dic !== '' && !isValidDic(dic)) {
    problems.push({ kind: 'dic-length', value: dic })
  }

  const icDph = (supplier.icDph ?? '').trim()
  if (icDph !== '' && !isValidIcDph(icDph)) {
    problems.push({ kind: 'ic-dph-shape', value: icDph })
  } else if (icDph !== '' && !icDphMatchesDic(icDph, dic)) {
    problems.push({ kind: 'ic-dph-mismatch', icDph, expected: expectedIcDph(dic) })
  }

  const iban = (supplier.iban ?? '').trim()
  if (iban !== '') {
    if (!isValidIban(iban)) {
      problems.push({ kind: 'iban-checksum', value: iban })
    } else if (iban.replace(/\s/g, '').toUpperCase().startsWith('SK') && !isSlovakIban(iban)) {
      problems.push({ kind: 'iban-length', value: iban })
    }
  }

  return problems
}

/** How much of a supplier is filled in — the invoice importer leaves stubs. */
export interface Completeness {
  filled: number
  total: number
  /** The fields a transfer and a tax filing need, specifically. */
  missingForPayment: string[]
}

const PAYMENT_FIELDS: readonly { key: keyof Supplier; label: string }[] = [
  { key: 'ico', label: tt('IČO') },
  { key: 'dic', label: tt('DIČ') },
  { key: 'iban', label: tt('IBAN') },
] as const

const ALL_FIELDS: readonly (keyof Supplier)[] = [
  'name',
  'ico',
  'dic',
  'icDph',
  'street',
  'city',
  'zip',
  'phone',
  'email',
  'bankName',
  'iban',
] as const

export function completeness(supplier: SupplierDraft): Completeness {
  const isFilled = (key: keyof Supplier) => String(supplier[key] ?? '').trim() !== ''
  return {
    filled: ALL_FIELDS.filter(isFilled).length,
    total: ALL_FIELDS.length,
    missingForPayment: PAYMENT_FIELDS.filter((field) => !isFilled(field.key)).map(
      (field) => field.label,
    ),
  }
}

export function matchesSearch(supplier: SupplierDraft, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [supplier.name, supplier.ico, supplier.city, supplier.email, supplier.notes].some(
    (field) => String(field ?? '').toLowerCase().includes(needle),
  )
}

// Generic, so it hands back what it was given: the page filters rows the API answered and keeps
// `Supplier[]`, while a test may filter drafts. Narrowing either end would have forced a cast at the
// other.
export function filterSuppliers<T extends SupplierDraft>(
  suppliers: T[],
  search: string,
  showInactive: boolean,
): T[] {
  return suppliers.filter((supplier) => {
    if (!showInactive && supplier.active === false) return false
    return matchesSearch(supplier, search)
  })
}

/**
 * Suppliers with something wrong first, then inactive last, then by name.
 *
 * A record whose IBAN does not check out is the one to look at, and legacy lists them
 * in whatever order the API returned.
 */
export function byAttention<T extends SupplierDraft>(suppliers: T[]): T[] {
  return [...suppliers].sort((left, right) => {
    const leftBad = identityProblems(left).length > 0 ? 0 : 1
    const rightBad = identityProblems(right).length > 0 ? 0 : 1
    if (leftBad !== rightBad) return leftBad - rightBad

    const leftActive = left.active === false ? 1 : 0
    const rightActive = right.active === false ? 1 : 0
    if (leftActive !== rightActive) return leftActive - rightActive

    return (left.name ?? '').localeCompare(right.name ?? '', 'sk')
  })
}

/** The body `POST /suppliers` and `PATCH /suppliers/{id}` want. */
export function supplierPayload(form: SupplierDraft): Record<string, unknown> {
  // Narrowly typed on purpose: `String(unknown)` on an object yields
  // "[object Object]", and every field here is a scalar.
  const text = (value: string | number | null | undefined): string | null => {
    if (value === null || value === undefined) return null
    const trimmed = String(value).trim()
    return trimmed === '' ? null : trimmed
  }

  return {
    name: String(form.name ?? '').trim(),
    ico: text(form.ico),
    dic: text(form.dic),
    icDph: text(form.icDph),
    street: text(form.street),
    city: text(form.city),
    zip: text(form.zip),
    country: text(form.country) ?? 'SK',
    phone: text(form.phone),
    email: text(form.email),
    website: text(form.website),
    bankName: text(form.bankName),
    bankSwift: text(form.bankSwift),
    // Stored without the display grouping, so two records of the same account are
    // the same string.
    iban: form.iban ? (form.iban.replace(/\s/g, '').toUpperCase() || null) : null,
    bankAccount: text(form.bankAccount),
    bankCode: text(form.bankCode),
    licenseAlcohol: text(form.licenseAlcohol),
    defaultPaymentForm: text(form.defaultPaymentForm),
    defaultConstantSymbol: text(form.defaultConstantSymbol),
    notes: text(form.notes),
    active: form.active !== false,
  }
}

export interface SupplierItemRow {
  id?: number
  supplierId?: number
  stockItemId?: number
  stockItemName?: string | null
  supplierCode?: string
  supplierName?: string | null
  defaultUnit?: string | null
  lastPrice?: number | null
  lastSeenAt?: string | null
}

export function matchesItemSearch(row: SupplierItemRow, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [row.supplierCode, row.stockItemName, row.supplierName].some((field) =>
    String(field ?? '').toLowerCase().includes(needle),
  )
}

/**
 * A supplier code appearing twice for one supplier.
 *
 * The mapping's whole job is to turn one code on one invoice into one stock item, so
 * two rows for the same code make the next import's outcome a coin toss. Legacy lets
 * both exist and shows no sign of it.
 */
export function duplicateCodes(rows: SupplierItemRow[]): string[] {
  const seen = new Map<string, number>()
  for (const row of rows) {
    const code = (row.supplierCode ?? '').trim().toLowerCase()
    if (code === '') continue
    seen.set(code, (seen.get(code) ?? 0) + 1)
  }
  return [...seen.entries()]
    .filter(([, count]) => count > 1)
    .map(([code]) => code)
    .sort()
}
